// src/components/messaging/ThreadCollaborationHeader.tsx

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  useGetThreadParticipantsQuery,
  useGetThreadPreferencesQuery,
  useUpdateThreadPreferencesMutation,
  useGetPinnedMessagesQuery,
  useUnpinMessageMutation,
  threadCollaborationApi,
} from '../../api/threadCollaborationApi';
import type { ThreadParticipant } from '../../types/api';
import SeenByIndicator from './SeenByIndicator';
import { realtimeClient } from '../../realtime/socketClient';
import { RealtimeEventType, type ThreadParticipantSeenPayload } from '../../realtime/events';

interface ThreadCollaborationHeaderProps {
  threadId: string;
  workspaceId: string;
  lastMessageAt?: string | null;
  currentUserId?: string;
  onJumpToMessage?: (messageId: string) => void;
}

export default function ThreadCollaborationHeader({
  threadId,
  workspaceId,
  lastMessageAt,
  currentUserId,
  onJumpToMessage,
}: ThreadCollaborationHeaderProps) {
  const [showParticipants, setShowParticipants] = useState(false);
  const [showPinnedBoard, setShowPinnedBoard] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // Fetch data
  const { data: participants = [] } = useGetThreadParticipantsQuery(
    { threadId, workspaceId },
    { skip: !threadId || !workspaceId }
  );

  // Subscribe to realtime THREAD_PARTICIPANT_SEEN events to update cache instantly
  useEffect(() => {
    if (!workspaceId || !threadId) return;

    const topic = `workspace:${workspaceId}`;

    const handleParticipantSeen = (payload: ThreadParticipantSeenPayload) => {
      if (payload.threadId !== threadId) return;

      // Patch the participants cache with the new lastSeenAt
      dispatch(
        threadCollaborationApi.util.updateQueryData(
          'getThreadParticipants',
          { threadId, workspaceId },
          (draft: ThreadParticipant[]) => {
            const participant = draft.find((p) => p.userId === payload.userId);
            if (participant) {
              participant.lastSeenAt = payload.lastSeenAt;
            }
          }
        )
      );
    };

    const unsubscribe = realtimeClient.on<ThreadParticipantSeenPayload>(
      topic,
      handleParticipantSeen,
      RealtimeEventType.THREAD_PARTICIPANT_SEEN
    );

    return () => {
      unsubscribe();
    };
  }, [workspaceId, threadId, dispatch]);

  const { data: preferences } = useGetThreadPreferencesQuery(
    { threadId, workspaceId },
    { skip: !threadId || !workspaceId }
  );

  const { data: pinnedMessages = [] } = useGetPinnedMessagesQuery(
    { threadId, workspaceId },
    { skip: !threadId || !workspaceId }
  );

  const [updatePreferences, { isLoading: isUpdatingPreferences }] =
    useUpdateThreadPreferencesMutation();
  const [unpinMessage] = useUnpinMessageMutation();

  const handleToggleMute = async () => {
    await updatePreferences({
      threadId,
      workspaceId,
      isMuted: !preferences?.isMuted,
    });
  };

  const handleToggleFollow = async () => {
    await updatePreferences({
      threadId,
      workspaceId,
      isFollowing: !preferences?.isFollowing,
    });
  };

  const handleUnpin = async (messageId: string) => {
    await unpinMessage({ messageId, workspaceId });
  };

  const formatExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return 'Forever';
    const date = new Date(expiresAt);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d left`;
    if (hours > 0) return `${hours}h left`;
    return 'Expiring soon';
  };

  return (
    <div className="flex items-center gap-2 py-2">
      {/* Seen By Indicator */}
      {lastMessageAt && (
        <SeenByIndicator
          participants={participants}
          lastMessageAt={lastMessageAt}
          currentUserId={currentUserId}
        />
      )}

      {/* Divider - only show if SeenBy is visible */}
      {lastMessageAt && participants.some((p) =>
        p.lastSeenAt && p.userId !== currentUserId &&
        new Date(p.lastSeenAt).getTime() >= new Date(lastMessageAt).getTime()
      ) && (
        <div className="w-px h-5 bg-gray-200" />
      )}

      {/* Participants */}
      <div className="relative">
        <button
          onClick={() => setShowParticipants(!showParticipants)}
          className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="View participants"
        >
          {/* Stacked avatars */}
          <div className="flex -space-x-2">
            {participants.slice(0, 3).map((p, i) => (
              <div
                key={p.id}
                className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600"
                style={{ zIndex: 3 - i }}
              >
                {p.user?.fullName?.charAt(0) || '?'}
              </div>
            ))}
            {participants.length > 3 && (
              <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-500">
                +{participants.length - 3}
              </div>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {participants.length} {participants.length === 1 ? 'participant' : 'participants'}
          </span>
        </button>

        {/* Participants dropdown */}
        {showParticipants && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
            <div className="p-3 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900">Participants</h4>
            </div>
            <ul className="max-h-48 overflow-y-auto">
              {participants.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
                    {p.user?.fullName?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {p.user?.fullName || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{p.role}</p>
                  </div>
                  {/* Online status indicator could go here */}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-gray-200" />

      {/* Mute Button */}
      <button
        onClick={handleToggleMute}
        disabled={isUpdatingPreferences}
        className={`flex items-center gap-1.5 px-2 py-1 text-sm rounded-lg transition-colors ${
          preferences?.isMuted
            ? 'bg-gray-200 text-gray-700'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        title={preferences?.isMuted ? 'Unmute thread' : 'Mute thread'}
      >
        {preferences?.isMuted ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
            />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
        )}
        <span className="text-xs">{preferences?.isMuted ? 'Muted' : 'Mute'}</span>
      </button>

      {/* Follow Button */}
      <button
        onClick={handleToggleFollow}
        disabled={isUpdatingPreferences}
        className={`flex items-center gap-1.5 px-2 py-1 text-sm rounded-lg transition-colors ${
          preferences?.isFollowing
            ? 'bg-blue-100 text-blue-700'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        title={preferences?.isFollowing ? 'Unfollow thread' : 'Follow thread'}
      >
        {preferences?.isFollowing ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        )}
        <span className="text-xs">{preferences?.isFollowing ? 'Following' : 'Follow'}</span>
      </button>

      {/* Divider */}
      <div className="w-px h-5 bg-gray-200" />

      {/* Pinned Messages */}
      <div className="relative">
        <button
          onClick={() => setShowPinnedBoard(!showPinnedBoard)}
          className={`flex items-center gap-1.5 px-2 py-1 text-sm rounded-lg transition-colors ${
            pinnedMessages.length > 0
              ? 'bg-yellow-100 text-yellow-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          title="View pinned messages"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
            />
          </svg>
          <span className="text-xs">
            {pinnedMessages.length > 0 ? `${pinnedMessages.length} pinned` : 'Pins'}
          </span>
        </button>

        {/* Pinned messages board */}
        {showPinnedBoard && (
          <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Pinned Messages</h4>
              <span className="text-xs text-gray-500">{pinnedMessages.length} items</span>
            </div>
            {pinnedMessages.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <svg
                  className="w-8 h-8 mx-auto mb-2 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                <p className="text-sm">No pinned messages</p>
                <p className="text-xs text-gray-400 mt-1">
                  Pin important messages to keep them visible
                </p>
              </div>
            ) : (
              <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {pinnedMessages.map((pin) => (
                  <li key={pin.id} className="p-3 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 line-clamp-2">
                          {pin.message?.bodyText ||
                            pin.message?.subject ||
                            'Message content'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            by {pin.pinnedBy?.fullName || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatExpiry(pin.expiresAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {onJumpToMessage && (
                          <button
                            onClick={() => onJumpToMessage(pin.messageId)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Jump to message"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                              />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleUnpin(pin.messageId)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Unpin message"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
